/**
 * Builds the FormBuilder Host field for a Google Meet interview.
 *
 * Same constraint as Zoom (see `buildScheduleHostField`): the host may only be
 * one of the picked interviewers or the scheduler — NOT an arbitrary account
 * user. The extra eligibility signal is the Google Meet scope: a person can
 * host only if they have granted Kula Meet access. The caller passes the set of
 * eligible ids (from `active_hosts?type=gmeet`); an interviewer/scheduler who
 * hasn't granted it is shown but DISABLED with a "No Google Meet access" chip,
 * mirroring Zoom's can't-host rows.
 *
 * Mount only when `location === 'google_meet'` AND `tGoogleMeetExternalHost` is
 * on. Pure builder — the caller (`useDetailsFormSchema`) supplies the picked
 * interviewers, the scheduler, and the eligible-id set (fetched by
 * `useGmeetHostSelection`).
 */
import {
  renderGmeetHostOption,
  renderGmeetHostTriggerValue,
} from '../fields/GmeetHostRenders';
import { makeHostDisabledTooltipWrapper } from '../fields/HostRenders';

export interface GmeetHostFieldOption {
  value: string;
  label: string;
  description?: string;
  image?: string;
}

export interface BuildGmeetHostFieldArgs {
  /** Picked interviewers (same source the Zoom host field uses). */
  participantOptions: GmeetHostFieldOption[];
  /** The recruiter scheduling the interview (or, in edit, the creator). Always
   *  offered as a host candidate even when not an interviewer. */
  currentUser?: { id: number | string; label: string; image?: string };
  /** Edit-mode safety net: the saved host, surfaced only when it IS the live
   *  selection (mirrors the Zoom field's `savedHost` gate). */
  savedHost?: GmeetHostFieldOption;
  /** The form's live `host_id` — gates whether `savedHost` is added. */
  currentHostId?: string;
  /** Ids (as strings) of users who have granted the Google Meet scope. An
   *  option not in this set renders disabled. */
  meetEligibleIds: Set<string>;
  /** True when the eligibility fetch FAILED — `meetEligibleIds` is then an
   *  empty set that means "unknown", NOT "nobody qualifies". Every option is
   *  left enabled so one 500 doesn't tell every recruiter to re-authenticate a
   *  working calendar; the BE still falls back gracefully if a pick lacks the
   *  scope. */
  eligibilityUnknown?: boolean;
  loading?: boolean;
  /** When true, the field is LOCKED: self-schedule one-on-one with multiple
   *  interviewers — the actual interviewer isn't decided until the candidate
   *  picks a slot, so the host is forced to the organizer and the dropdown is
   *  disabled. Mirrors the Zoom host field's lock (buildScheduleHostField). */
  disabled?: boolean;
  /** Hover-tooltip copy shown when `disabled`. */
  disabledTooltip?: string;
}

const LABEL_TOOLTIP =
  'Only the person scheduling the interview or an interviewer can host. ' +
  'Interviewers need to re-authenticate their Google Calendar integration to host.';

// Locked-state copy — identical to the Zoom host field's DEFAULT_DISABLED_TOOLTIP
// so the message is consistent across providers.
const DISABLED_TOOLTIP =
  "For one-on-one interviews with multiple interviewers, the host can't be " +
  'changed because the interviewer is not assigned until the candidate picks ' +
  'a time slot.';

export function buildGmeetHostField({
  participantOptions,
  currentUser,
  savedHost,
  currentHostId,
  meetEligibleIds,
  eligibilityUnknown = false,
  loading = false,
  disabled = false,
  disabledTooltip = DISABLED_TOOLTIP,
}: BuildGmeetHostFieldArgs) {
  const seen = new Set<string>();
  const options: Array<
    GmeetHostFieldOption & { disabled?: boolean; meetEligible?: boolean }
  > = [];

  // An interviewer can host only with the Meet scope. The scheduler is ALWAYS a
  // valid host — they own the calendar event, so selecting them yields a
  // scheduler-hosted Meet on their own calendar (the BE fallback) regardless of
  // scope. So the scheduler is never disabled / never shows "Re-auth", even when
  // an interviewer has the scope.
  const schedulerId = currentUser ? String(currentUser.id) : '';
  const withEligibility = (o: GmeetHostFieldOption) => {
    const id = String(o.value);
    const eligible =
      eligibilityUnknown || meetEligibleIds.has(id) || id === schedulerId;
    return { ...o, meetEligible: eligible, disabled: !eligible };
  };

  // 1. Picked interviewers.
  for (const opt of participantOptions) {
    if (!opt.value || seen.has(opt.value)) continue;
    seen.add(opt.value);
    options.push(withEligibility(opt));
  }

  // 2. The scheduler, if not already an interviewer.
  if (currentUser) {
    const id = String(currentUser.id);
    if (id && !seen.has(id)) {
      seen.add(id);
      options.push(
        withEligibility({
          value: id,
          label: currentUser.label,
          image: currentUser.image,
        })
      );
    }
  }

  // 3. The saved host (edit) — only when it IS the live selection, so the
  //    Select can render the chip without leaking a non-interviewer as a
  //    re-selectable row. Mirrors `buildScheduleHostField`.
  if (
    savedHost?.value &&
    !seen.has(savedHost.value) &&
    String(savedHost.value) === String(currentHostId ?? '')
  ) {
    seen.add(savedHost.value);
    options.push(withEligibility(savedHost));
  }

  // When locked (self-schedule one-on-one + multi-interviewer) the host is the
  // organizer. Set it as defaultValue so FormBuilder's conditional-field reset
  // (Host is gated on location === 'google_meet') re-lands on the organizer
  // rather than empty; the dynamic transition is enforced by useGmeetHostSelection.
  const lockedDefault =
    disabled && currentUser ? String(currentUser.id) : undefined;

  return {
    type: 'select' as const,
    name: 'host_id',
    label: 'Host',
    placeholder: loading ? 'Loading hosts…' : 'Select host',
    required: 'Host is required',
    disabled,
    ...(lockedDefault ? { defaultValue: lockedDefault } : {}),
    options,
    tooltip: LABEL_TOOLTIP,
    // Locked-state hover tooltip rides on FormBuilder's field-level `wrapper`
    // (kula-ai Select has no tooltip prop), matching the Zoom host field.
    ...(disabled && disabledTooltip
      ? { wrapper: makeHostDisabledTooltipWrapper(disabledTooltip) }
      : {}),
    condition: {
      operator: 'and',
      conditions: [
        { operator: 'isNotEmpty', field: 'participant_ids' },
        { operator: 'equal', field: 'location', value: 'google_meet' },
      ],
    },
    fieldProps: {
      searchable: true,
      mode: 'single' as const,
      isClearable: false,
      renderOption: renderGmeetHostOption,
      renderTriggerValue: renderGmeetHostTriggerValue,
      noOptionsMessage:
        'No valid host found. Add an interviewer or the scheduler who has ' +
        'granted Google Meet access.',
    },
  };
}
